import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import {
  Plus, Users, Edit, Trash2, MapPin, Clock, UserCheck,
  UserPlus, X, Calendar, BarChart3, Home
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1'];
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const DIAS_LABEL = {
  domingo: 'Domingo', segunda: 'Segunda', terça: 'Terça',
  quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sábado: 'Sábado'
};

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (date) => {
  try { return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR }); } catch { return ''; }
};

const CELULA_FORM_INIT = {
  nome: '', endereco: '', bairro: '', cep: '', diaSemana: 'domingo', horario: '', lider: '', liderTreinamento: ''
};

const MemberAvatar = ({ name, size = 32 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    backgroundColor: getAvatarColor(name), color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.35, fontWeight: '600'
  }}>
    {getInitials(name)}
  </div>
);

const StatCard = ({ label, value }) => (
  <div className="card" style={{ flex: '1', minWidth: 80, padding: '12px 16px', textAlign: 'center' }}>
    <div style={{ fontSize: 22, fontWeight: 700, color: '#1f2937' }}>{value}</div>
    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{label}</div>
  </div>
);

const Celulas = () => {
  const { user, canAccessSecretariat, isPastor, isAdmin, isLiderCelula } = useAuth();
  const isGestao = canAccessSecretariat() || isPastor() || isAdmin();
  const isLider = isLiderCelula() && !isGestao;

  // --- Shared state ---
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [reunioes, setReunioes] = useState([]);

  // --- Gestão state ---
  const [celulas, setCelulas] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [view, setView] = useState('list');
  const [selectedCelula, setSelectedCelula] = useState(null);

  // --- Líder state ---
  const [minhaCelula, setMinhaCelula] = useState(null);

  // --- Modals ---
  const [showCelulaModal, setShowCelulaModal] = useState(false);
  const [celulaModalMode, setCelulaModalMode] = useState('create');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReuniaoModal, setShowReuniaoModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [celulaToDelete, setCelulaToDelete] = useState(null);
  const [membroToRemove, setMembroToRemove] = useState(null);
  const [savingCelula, setSavingCelula] = useState(false);
  const [savingReuniao, setSavingReuniao] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  // --- Forms ---
  const [celulaForm, setCelulaForm] = useState(CELULA_FORM_INIT);
  const [reuniaoForm, setReuniaoForm] = useState({ data: getTodayISO(), observacoes: '', presentes: [] });
  const [visitantesTemp, setVisitantesTemp] = useState([]);
  const [visitanteForm, setVisitanteForm] = useState({ name: '', phone: '', canSendMessage: 'não' });
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [showNovoMembroModal, setShowNovoMembroModal] = useState(false);
  const [novoMembroForm, setNovoMembroForm] = useState({ name: '', email: '', phone: '', birthDate: '' });
  const [savingNovoMembro, setSavingNovoMembro] = useState(false);

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const fetchCelulas = useCallback(async () => {
    const res = await api.get('/celulas');
    setCelulas(res.data);
  }, []);

  const fetchRelatorios = useCallback(async () => {
    const res = await api.get('/celulas/relatorios');
    setRelatorios(res.data);
  }, []);

  const fetchMinhaCelula = useCallback(async () => {
    try {
      const res = await api.get('/celulas/minha');
      setMinhaCelula(res.data);
    } catch (err) {
      if (err.response?.status !== 404) throw err;
    }
  }, []);

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await api.get('/celulas/usuarios');
      setUsuarios(res.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchReunioes = useCallback(async (celulaId) => {
    const res = await api.get(`/celulas/${celulaId}/reunioes`);
    setReunioes(res.data);
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (isGestao) {
          await Promise.all([fetchCelulas(), fetchUsuarios()]);
        } else if (isLider) {
          await Promise.all([fetchMinhaCelula(), fetchUsuarios()]);
        }
      } catch {
        toast.error('Erro ao carregar dados');
      }
      setLoading(false);
    };
    init();
  }, [isGestao, isLider, fetchCelulas, fetchMinhaCelula, fetchUsuarios]);

  useEffect(() => {
    if (!loading && isLider && minhaCelula) {
      fetchReunioes(minhaCelula._id).catch(() => {});
    }
  }, [loading, isLider, minhaCelula, fetchReunioes]);

  // ── Gestão handlers ────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setCelulaForm(CELULA_FORM_INIT);
    setCelulaModalMode('create');
    setShowCelulaModal(true);
  };

  const handleCepChange = async (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    const formatted = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    setCelulaForm((p) => ({ ...p, cep: formatted }));
    if (digits.length === 8) {
      try {
        setCepLoading(true);
        const res = await axios.get(`https://viacep.com.br/ws/${digits}/json/`);
        if (!res.data.erro) {
          setCelulaForm((p) => ({
            ...p,
            endereco: res.data.logradouro || p.endereco,
            bairro: res.data.bairro || p.bairro,
          }));
        } else {
          toast.warn('CEP não encontrado');
        }
      } catch {
        toast.error('Erro ao buscar CEP');
      } finally {
        setCepLoading(false);
      }
    }
  };

  const openEditModal = (celula, e) => {
    e?.stopPropagation();
    setCelulaForm({
      nome: celula.nome || '',
      endereco: celula.endereco || '',
      bairro: celula.bairro || '',
      cep: celula.cep || '',
      diaSemana: celula.diaSemana || 'domingo',
      horario: celula.horario || '',
      lider: celula.lider?._id || celula.lider || '',
      liderTreinamento: celula.liderTreinamento?._id || celula.liderTreinamento || ''
    });
    setSelectedCelula(celula);
    setCelulaModalMode('edit');
    setShowCelulaModal(true);
  };

  const openDetailModal = async (celula) => {
    setSelectedCelula(celula);
    setReunioes([]);
    setShowDetailModal(true);
    setShowAddMember(false);
    try { await fetchReunioes(celula._id); } catch { toast.error('Erro ao carregar reuniões'); }
  };

  const handleSaveCelula = async (e) => {
    e.preventDefault();
    if (!celulaForm.nome || !celulaForm.diaSemana || !celulaForm.horario || !celulaForm.lider) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setSavingCelula(true);
    try {
      if (celulaModalMode === 'create') {
        const res = await api.post('/celulas', celulaForm);
        setCelulas((prev) => [...prev, res.data]);
        toast.success('Célula criada com sucesso!');
      } else {
        const res = await api.put(`/celulas/${selectedCelula._id}`, celulaForm);
        setCelulas((prev) => prev.map((c) => (c._id === res.data._id ? res.data : c)));
        if (showDetailModal) setSelectedCelula(res.data);
        toast.success('Célula atualizada com sucesso!');
      }
      setShowCelulaModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao salvar célula');
    } finally {
      setSavingCelula(false);
    }
  };

  const confirmDelete = (celula, e) => {
    e?.stopPropagation();
    setCelulaToDelete(celula);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCelula = async () => {
    try {
      await api.delete(`/celulas/${celulaToDelete._id}`);
      setCelulas((prev) => prev.filter((c) => c._id !== celulaToDelete._id));
      if (selectedCelula?._id === celulaToDelete._id) setShowDetailModal(false);
      toast.success('Célula removida com sucesso!');
    } catch {
      toast.error('Erro ao remover célula');
    }
    setCelulaToDelete(null);
  };

  const handleAddMembro = async (celulaId, userId) => {
    try {
      const res = await api.post(`/celulas/${celulaId}/membros`, { userId });
      const membros = res.data.membros;
      if (isGestao) {
        setCelulas((prev) => prev.map((c) => (c._id === celulaId ? { ...c, membros } : c)));
        if (selectedCelula?._id === celulaId) setSelectedCelula((prev) => ({ ...prev, membros }));
      } else {
        setMinhaCelula((prev) => ({ ...prev, membros }));
      }
      setAddMemberSearch('');
      setShowAddMember(false);
      toast.success('Membro adicionado!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao adicionar membro');
    }
  };

  const handleRemoveMembro = async (celulaId, userId) => {
    try {
      await api.delete(`/celulas/${celulaId}/membros/${userId}`);
      const filterOut = (arr) =>
        (arr || []).filter((m) => (m._id?.toString() || m.toString()) !== userId.toString());
      if (isGestao) {
        setCelulas((prev) => prev.map((c) => (c._id === celulaId ? { ...c, membros: filterOut(c.membros) } : c)));
        if (selectedCelula?._id === celulaId)
          setSelectedCelula((prev) => ({ ...prev, membros: filterOut(prev.membros) }));
      } else {
        setMinhaCelula((prev) => ({ ...prev, membros: filterOut(prev.membros) }));
      }
      toast.success('Membro removido da célula');
    } catch {
      toast.error('Erro ao remover membro');
    }
  };

  const confirmRemoveMembro = () => {
    if (!membroToRemove) return;
    handleRemoveMembro(membroToRemove.celulaId, membroToRemove.userId);
    setMembroToRemove(null);
  };

  const handleSaveNovoMembro = async (e) => {
    e.preventDefault();
    if (!novoMembroForm.name || !novoMembroForm.email || !novoMembroForm.phone || !novoMembroForm.birthDate) {
      toast.error('Preencha todos os campos');
      return;
    }
    setSavingNovoMembro(true);
    try {
      await api.post('/celulas/criar-membro', novoMembroForm);
      toast.success('Membro criado com sucesso!');
      setNovoMembroForm({ name: '', email: '', phone: '', birthDate: '' });
      setShowNovoMembroModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao criar membro');
    } finally {
      setSavingNovoMembro(false);
    }
  };

  // ── Meeting handlers ───────────────────────────────────────────────────────

  const openReuniaoModal = () => {
    setReuniaoForm({ data: getTodayISO(), observacoes: '', presentes: [] });
    setVisitantesTemp([]);
    setVisitanteForm({ name: '', phone: '', canSendMessage: 'não' });
    setShowReuniaoModal(true);
  };

  const togglePresente = (memberId) => {
    const id = memberId?.toString();
    setReuniaoForm((prev) => ({
      ...prev,
      presentes: prev.presentes.includes(id)
        ? prev.presentes.filter((x) => x !== id)
        : [...prev.presentes, id]
    }));
  };

  const addVisitante = () => {
    if (!visitanteForm.name.trim()) { toast.error('Nome do visitante é obrigatório'); return; }
    setVisitantesTemp((prev) => [...prev, { ...visitanteForm, tempId: Date.now() }]);
    setVisitanteForm({ name: '', phone: '', canSendMessage: 'não' });
  };

  const removeVisitante = (tempId) => setVisitantesTemp((prev) => prev.filter((v) => v.tempId !== tempId));

  const handleSaveReuniao = async () => {
    const celulaId = isGestao ? selectedCelula?._id : minhaCelula?._id;
    if (!celulaId) return;
    if (!reuniaoForm.data) { toast.error('Informe a data da reunião'); return; }
    setSavingReuniao(true);
    try {
      const res = await api.post(`/celulas/${celulaId}/reunioes`, {
        data: reuniaoForm.data,
        presentes: reuniaoForm.presentes,
        visitantes: visitantesTemp.map(({ name, phone, canSendMessage }) => ({ name, phone, canSendMessage })),
        observacoes: reuniaoForm.observacoes
      });
      setReunioes((prev) => [res.data, ...prev]);
      setShowReuniaoModal(false);
      toast.success('Reunião registrada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao registrar reunião');
    } finally {
      setSavingReuniao(false);
    }
  };

  // ── Helper: available members ──────────────────────────────────────────────

  const membrosDisponiveis = (celula) => {
    const ids = new Set((celula?.membros || []).map((m) => m._id?.toString() || m.toString()));
    const filtered = usuarios.filter((u) => !ids.has(u._id));
    if (!addMemberSearch) return filtered.slice(0, 10);
    const s = addMemberSearch.toLowerCase();
    return filtered.filter((u) => u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s)).slice(0, 10);
  };

  const activeCelulaMembros = isGestao ? (selectedCelula?.membros || []) : (minhaCelula?.membros || []);
  const activeCelulaId = isGestao ? selectedCelula?._id : minhaCelula?._id;

  // ── Shared subcomponents ───────────────────────────────────────────────────

  const MembersSection = ({ celula, context }) => (
    <div style={{ marginBottom: context === 'detail' ? 20 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Membros ({celula?.membros?.length || 0})</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => { setShowNovoMembroModal(true); }}>
            <Plus size={13} style={{ marginRight: 4 }} />Novo Membro
          </button>
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => setShowAddMember((v) => !v)}>
            <UserPlus size={13} style={{ marginRight: 4 }} />Adicionar
          </button>
        </div>
      </div>

      {showAddMember && (
        <div style={{ marginBottom: 12, padding: 12, backgroundColor: '#f0f9ff', borderRadius: 8 }}>
          <input className="form-input" placeholder="Buscar membro..."
            value={addMemberSearch} onChange={(e) => setAddMemberSearch(e.target.value)}
            style={{ marginBottom: 8 }} />
          <div style={{ maxHeight: 150, overflowY: 'auto' }}>
            {membrosDisponiveis(celula).map((u) => (
              <div key={u._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MemberAvatar name={u.name} size={24} />
                  <span style={{ fontSize: 13 }}>{u.name}</span>
                </div>
                <button className="btn btn-primary" style={{ fontSize: 11, padding: '3px 8px' }}
                  onClick={() => handleAddMembro(celula._id, u._id)}>
                  Adicionar
                </button>
              </div>
            ))}
            {membrosDisponiveis(celula).length === 0 && (
              <p style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: 8 }}>Nenhum membro disponível</p>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(celula?.membros || []).map((m) => (
          <div key={m._id || m} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', backgroundColor: '#f9fafb', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <MemberAvatar name={m.name} size={30} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</div>
                {m.phone && <div style={{ fontSize: 12, color: '#6b7280' }}>{m.phone}</div>}
              </div>
            </div>
            <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
              aria-label={`Remover ${m.name} da célula`}
              onClick={() => setMembroToRemove({ celulaId: celula._id, userId: m._id || m, nome: m.name })}>
              <X size={14} />
            </button>
          </div>
        ))}
        {(!celula?.membros || celula.membros.length === 0) && (
          <p style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: 16 }}>Nenhum membro cadastrado ainda.</p>
        )}
      </div>
    </div>
  );

  const NovoMembroModal = () => (
    <Modal isOpen={showNovoMembroModal} onClose={() => setShowNovoMembroModal(false)} title="Novo Membro" size="sm">
      <form onSubmit={handleSaveNovoMembro}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="form-label" htmlFor="novo-membro-name">Nome completo *</label>
            <input id="novo-membro-name" className="form-input" placeholder="Nome do membro" value={novoMembroForm.name}
              onChange={(e) => setNovoMembroForm((p) => ({ ...p, name: e.target.value }))} required />
          </div>
          <div>
            <label className="form-label" htmlFor="novo-membro-email">Email *</label>
            <input id="novo-membro-email" type="email" className="form-input" placeholder="email@exemplo.com" value={novoMembroForm.email}
              onChange={(e) => setNovoMembroForm((p) => ({ ...p, email: e.target.value }))} required />
          </div>
          <div>
            <label className="form-label" htmlFor="novo-membro-phone">Telefone *</label>
            <input id="novo-membro-phone" className="form-input" placeholder="(00) 00000-0000" value={novoMembroForm.phone}
              onChange={(e) => setNovoMembroForm((p) => ({ ...p, phone: e.target.value }))} required />
          </div>
          <div>
            <label className="form-label" htmlFor="novo-membro-birthdate">Data de Nascimento *</label>
            <input id="novo-membro-birthdate" type="date" className="form-input" value={novoMembroForm.birthDate}
              onChange={(e) => setNovoMembroForm((p) => ({ ...p, birthDate: e.target.value }))} required />
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
            A senha inicial será <strong>apascentar</strong>. O membro pode alterá-la no primeiro acesso.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" className="btn btn-outline" onClick={() => setShowNovoMembroModal(false)}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={savingNovoMembro}>
            {savingNovoMembro ? 'Criando...' : 'Criar Membro'}
          </button>
        </div>
      </form>
    </Modal>
  );

  const ReuniaoModal = () => (
    <Modal isOpen={showReuniaoModal} onClose={() => setShowReuniaoModal(false)} title="Registrar Reunião" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label className="form-label" htmlFor="reuniao-data">Data da Reunião *</label>
          <input id="reuniao-data" type="date" className="form-input" value={reuniaoForm.data}
            onChange={(e) => setReuniaoForm((p) => ({ ...p, data: e.target.value }))} />
        </div>

        <div>
          <label className="form-label">Presença</label>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            {activeCelulaMembros.map((m) => {
              const id = m._id?.toString() || m.toString();
              return (
                <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', backgroundColor: reuniaoForm.presentes.includes(id) ? '#eff6ff' : 'white' }}>
                  <input type="checkbox" checked={reuniaoForm.presentes.includes(id)} onChange={() => togglePresente(id)} />
                  <MemberAvatar name={m.name} size={24} />
                  <span style={{ fontSize: 13 }}>{m.name}</span>
                </label>
              );
            })}
            {activeCelulaMembros.length === 0 && (
              <p style={{ padding: 12, fontSize: 13, color: '#6b7280', textAlign: 'center' }}>Nenhum membro na célula</p>
            )}
          </div>
        </div>

        <div>
          <label className="form-label">Visitantes</label>
          {visitantesTemp.length > 0 && (
            <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {visitantesTemp.map((v) => (
                <div key={v.tempId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#f0fdf4', borderRadius: 6 }}>
                  <span style={{ fontSize: 13 }}>{v.name}{v.phone ? ` · ${v.phone}` : ''}</span>
                  <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444' }} aria-label={`Remover ${v.name} da lista`} onClick={() => removeVisitante(v.tempId)}><X size={13} /></button>
                </div>
              ))}
            </div>
          )}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input className="form-input" placeholder="Nome do visitante *" aria-label="Nome do visitante" value={visitanteForm.name}
                onChange={(e) => setVisitanteForm((p) => ({ ...p, name: e.target.value }))} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
                <input className="form-input" placeholder="Telefone" aria-label="Telefone do visitante" value={visitanteForm.phone}
                  onChange={(e) => setVisitanteForm((p) => ({ ...p, phone: e.target.value }))} />
                <select className="form-input" style={{ width: 'auto' }} aria-label="Pode enviar mensagem" value={visitanteForm.canSendMessage}
                  onChange={(e) => setVisitanteForm((p) => ({ ...p, canSendMessage: e.target.value }))}>
                  <option value="sim">Pode msg</option>
                  <option value="não">Não msg</option>
                </select>
              </div>
              <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} onClick={addVisitante}>
                <UserPlus size={13} style={{ marginRight: 4 }} />Adicionar Visitante
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="form-label" htmlFor="reuniao-observacoes">Observações</label>
          <textarea id="reuniao-observacoes" className="form-input" rows={3} value={reuniaoForm.observacoes}
            onChange={(e) => setReuniaoForm((p) => ({ ...p, observacoes: e.target.value }))}
            placeholder="Observações da reunião..." style={{ resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => setShowReuniaoModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSaveReuniao} disabled={savingReuniao}>
            {savingReuniao ? 'Salvando...' : 'Registrar Reunião'}
          </button>
        </div>
      </div>
    </Modal>
  );

  // ── Early returns ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <div className="loading" />
        <span style={{ marginLeft: 8 }}>Carregando...</span>
      </div>
    );
  }

  if (!isGestao && !isLider) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
        <p>Você não tem acesso ao módulo Células.</p>
      </div>
    );
  }

  // ── GESTÃO VIEW ────────────────────────────────────────────────────────────

  if (isGestao) {
    const totalMembros = celulas.reduce((sum, c) => sum + (c.membros?.length || 0), 0);
    const ativas = celulas.filter((c) => c.ativa).length;

    return (
      <div style={{ padding: '16px', paddingBottom: 80 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: 0 }}>Células</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={`btn ${view === 'relatorios' ? 'btn-primary' : 'btn-outline'}`}
              style={{ fontSize: 13, padding: '8px 12px' }}
              onClick={() => {
                const next = view === 'relatorios' ? 'list' : 'relatorios';
                setView(next);
                if (next === 'relatorios') fetchRelatorios().catch(() => toast.error('Erro ao carregar relatórios'));
              }}
            >
              <BarChart3 size={14} style={{ marginRight: 4 }} />Relatórios
            </button>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '8px 12px' }} onClick={openCreateModal}>
              <Plus size={14} style={{ marginRight: 4 }} />Nova Célula
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <StatCard label="Total" value={celulas.length} />
          <StatCard label="Membros" value={totalMembros} />
          <StatCard label="Ativas" value={ativas} />
        </div>

        {/* LIST */}
        {view === 'list' && (
          <>
            {celulas.length === 0 && (
              <div className="card" style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
                <Users size={40} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
                <p>Nenhuma célula cadastrada ainda.</p>
                <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={openCreateModal}>
                  <Plus size={14} style={{ marginRight: 4 }} />Nova Célula
                </button>
              </div>
            )}
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {celulas.map((celula) => (
                <div key={celula._id} className="card" style={{ padding: 16, cursor: 'pointer' }}
                  onClick={() => openDetailModal(celula)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: 0 }}>{celula.nome}</h3>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      backgroundColor: celula.ativa ? '#d1fae5' : '#fee2e2',
                      color: celula.ativa ? '#065f46' : '#991b1b'
                    }}>
                      {celula.ativa ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>

                  {(celula.bairro || celula.endereco) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: 13, marginBottom: 4 }}>
                      <MapPin size={13} />
                      <span>{[celula.bairro, celula.endereco].filter(Boolean).join(' — ')}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: 13, marginBottom: 10 }}>
                    <Clock size={13} />
                    <span style={{ textTransform: 'capitalize' }}>{DIAS_LABEL[celula.diaSemana]}s às {celula.horario}</span>
                  </div>

                  {celula.lider && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                      <MemberAvatar name={celula.lider.name} size={22} />
                      <span style={{ fontSize: 13, color: '#374151' }}>{celula.lider.name}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>Líder</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
                    <span style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Users size={13} />{celula.membros?.length || 0} membros
                    </span>
                    <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 8px' }}
                        aria-label={`Editar célula ${celula.nome}`}
                        onClick={(e) => openEditModal(celula, e)}>
                        <Edit size={12} />
                      </button>
                      <button className="btn btn-danger" style={{ fontSize: 12, padding: '4px 8px' }}
                        aria-label={`Excluir célula ${celula.nome}`}
                        onClick={(e) => confirmDelete(celula, e)}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* RELATÓRIOS */}
        {view === 'relatorios' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    {['Célula', 'Líder', 'Membros', 'Reuniões', 'Última Reunião', 'Méd. Presença'].map((h) => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {relatorios.map((r, i) => (
                    <tr key={r._id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 600 }}>{r.nome}</div>
                        {r.bairro && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.bairro}</div>}
                      </td>
                      <td style={{ padding: '10px 12px' }}>{r.lider?.name || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{r.totalMembros}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{r.totalReunioes}</td>
                      <td style={{ padding: '10px 12px' }}>{r.ultimaReuniao ? formatDate(r.ultimaReuniao) : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{r.mediaPresenca || '—'}</td>
                    </tr>
                  ))}
                  {relatorios.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Nenhum dado disponível</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MODAL: Criar/Editar */}
        <Modal isOpen={showCelulaModal} onClose={() => setShowCelulaModal(false)}
          title={celulaModalMode === 'create' ? 'Nova Célula' : 'Editar Célula'} size="md">
          <form onSubmit={handleSaveCelula}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label" htmlFor="celula-nome">Nome da Célula *</label>
                <input id="celula-nome" className="form-input" value={celulaForm.nome}
                  onChange={(e) => setCelulaForm((p) => ({ ...p, nome: e.target.value }))}
                  placeholder="Ex: Célula Esperança" required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label" htmlFor="celula-dia-semana">Dia da Semana *</label>
                  <select id="celula-dia-semana" className="form-input" value={celulaForm.diaSemana}
                    onChange={(e) => setCelulaForm((p) => ({ ...p, diaSemana: e.target.value }))}>
                    {DIAS.map((d) => <option key={d} value={d}>{DIAS_LABEL[d]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" htmlFor="celula-horario">Horário *</label>
                  <input id="celula-horario" className="form-input" value={celulaForm.horario}
                    onChange={(e) => setCelulaForm((p) => ({ ...p, horario: e.target.value }))}
                    placeholder="Ex: 19:30" required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label" htmlFor="celula-cep">CEP</label>
                  <input id="celula-cep" className="form-input" value={celulaForm.cep}
                    onChange={handleCepChange}
                    placeholder={cepLoading ? 'Buscando...' : '00000-000'}
                    maxLength={9} />
                </div>
                <div />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label" htmlFor="celula-bairro">Bairro</label>
                  <input id="celula-bairro" className="form-input" value={celulaForm.bairro}
                    onChange={(e) => setCelulaForm((p) => ({ ...p, bairro: e.target.value }))}
                    placeholder="Bairro" />
                </div>
                <div>
                  <label className="form-label" htmlFor="celula-endereco">Endereço</label>
                  <input id="celula-endereco" className="form-input" value={celulaForm.endereco}
                    onChange={(e) => setCelulaForm((p) => ({ ...p, endereco: e.target.value }))}
                    placeholder="Rua, nº" />
                </div>
              </div>
              <div>
                <label className="form-label" htmlFor="celula-lider">Líder *</label>
                <select id="celula-lider" className="form-input" value={celulaForm.lider}
                  onChange={(e) => setCelulaForm((p) => ({ ...p, lider: e.target.value }))} required>
                  <option value="">Selecione o líder</option>
                  {usuarios.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" htmlFor="celula-lider-treinamento">Líder de Treinamento</label>
                <select id="celula-lider-treinamento" className="form-input" value={celulaForm.liderTreinamento}
                  onChange={(e) => setCelulaForm((p) => ({ ...p, liderTreinamento: e.target.value }))}>
                  <option value="">Nenhum</option>
                  {usuarios.filter((u) => u._id !== celulaForm.lider).map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button type="button" className="btn btn-outline" onClick={() => setShowCelulaModal(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={savingCelula}>
                {savingCelula ? 'Salvando...' : celulaModalMode === 'create' ? 'Criar Célula' : 'Salvar'}
              </button>
            </div>
          </form>
        </Modal>

        {/* MODAL: Detalhe */}
        <Modal isOpen={showDetailModal && !!selectedCelula}
          onClose={() => { setShowDetailModal(false); setShowAddMember(false); setAddMemberSearch(''); }}
          title={selectedCelula?.nome} size="lg">
          {selectedCelula && (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, padding: 12, backgroundColor: '#f9fafb', borderRadius: 8 }}>
                {(selectedCelula.bairro || selectedCelula.endereco) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' }}>
                    <MapPin size={13} />{[selectedCelula.bairro, selectedCelula.endereco].filter(Boolean).join(' — ')}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' }}>
                  <Clock size={13} />
                  <span style={{ textTransform: 'capitalize' }}>{DIAS_LABEL[selectedCelula.diaSemana]}s às {selectedCelula.horario}</span>
                </div>
                <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 8px', marginLeft: 'auto' }}
                  onClick={() => { setShowDetailModal(false); openEditModal(selectedCelula); }}>
                  <Edit size={12} style={{ marginRight: 4 }} />Editar
                </button>
              </div>

              {MembersSection({ celula: selectedCelula, context: 'detail' })}

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Reuniões ({reunioes.length})</h3>
                  <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={openReuniaoModal}>
                    <Plus size={13} style={{ marginRight: 4 }} />Registrar
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
                  {reunioes.map((r) => (
                    <div key={r._id} style={{ padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: 8, borderLeft: '3px solid #6366f1' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{formatDate(r.data)}</span>
                        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#6b7280' }}>
                          <span>{r.presentes?.length || 0} presentes</span>
                          {(r.visitantes?.length || 0) > 0 && <span>· {r.visitantes.length} visitantes</span>}
                        </div>
                      </div>
                      {r.observacoes && <p style={{ fontSize: 12, color: '#374151', margin: '4px 0 0', lineHeight: 1.4 }}>{r.observacoes}</p>}
                    </div>
                  ))}
                  {reunioes.length === 0 && (
                    <p style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: 16 }}>Nenhuma reunião registrada</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal>

        {ReuniaoModal()}
        {NovoMembroModal()}

        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteCelula}
          title="Excluir Célula"
          message={`Tem certeza que deseja excluir "${celulaToDelete?.nome}"? Todas as reuniões serão removidas.`}
          confirmText="Excluir"
          variant="danger"
        />
        <ConfirmModal
          isOpen={!!membroToRemove}
          onClose={() => setMembroToRemove(null)}
          onConfirm={confirmRemoveMembro}
          title="Remover Membro"
          message={`Tem certeza que deseja remover "${membroToRemove?.nome}" da célula?`}
          confirmText="Remover"
          variant="danger"
        />
      </div>
    );
  }

  // ── LÍDER VIEW ─────────────────────────────────────────────────────────────

  if (!minhaCelula) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        <Home size={40} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
        <p style={{ fontWeight: 500 }}>Você ainda não está vinculado a nenhuma célula.</p>
        <p style={{ fontSize: 13, marginTop: 4 }}>Entre em contato com a secretaria.</p>
      </div>
    );
  }

  const celula = minhaCelula;

  return (
    <div style={{ padding: '16px', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: 0 }}>Minha Célula</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '2px 0 0' }}>{celula.nome}</p>
        </div>
        <button className="btn btn-primary" style={{ fontSize: 13, padding: '8px 12px' }} onClick={openReuniaoModal}>
          <Plus size={14} style={{ marginRight: 4 }} />Reunião
        </button>
      </div>

      {/* Cell info card */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {(celula.bairro || celula.endereco) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' }}>
              <MapPin size={14} />{[celula.bairro, celula.endereco].filter(Boolean).join(' — ')}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#374151' }}>
            <Clock size={14} />
            <span style={{ textTransform: 'capitalize' }}>{DIAS_LABEL[celula.diaSemana]}s às {celula.horario}</span>
          </div>
        </div>
        {celula.liderTreinamento && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
            <UserCheck size={14} color="#6366f1" />
            <span style={{ fontSize: 13, color: '#374151' }}>Líder de Treinamento: {celula.liderTreinamento.name}</span>
          </div>
        )}
      </div>

      {/* Members */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        {MembersSection({ celula: celula, context: 'lider' })}
      </div>

      {/* Meetings */}
      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Reuniões ({reunioes.length})</h2>
        {reunioes.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>
            <Calendar size={36} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
            <p style={{ fontSize: 13 }}>Nenhuma reunião registrada ainda.</p>
            <button className="btn btn-primary" style={{ marginTop: 8, fontSize: 13 }} onClick={openReuniaoModal}>
              <Plus size={13} style={{ marginRight: 4 }} />Registrar primeira reunião
            </button>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reunioes.map((r) => (
            <div key={r._id} style={{ padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: 8, borderLeft: '3px solid #6366f1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{formatDate(r.data)}</span>
                <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#6b7280' }}>
                  <span>{r.presentes?.length || 0} presentes</span>
                  {(r.visitantes?.length || 0) > 0 && <span>· {r.visitantes.length} visitantes</span>}
                </div>
              </div>
              {r.observacoes && (
                <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.5 }}>{r.observacoes}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {ReuniaoModal()}
      {NovoMembroModal()}
      <ConfirmModal
        isOpen={!!membroToRemove}
        onClose={() => setMembroToRemove(null)}
        onConfirm={confirmRemoveMembro}
        title="Remover Membro"
        message={`Tem certeza que deseja remover "${membroToRemove?.nome}" da célula?`}
        confirmText="Remover"
        variant="danger"
      />
    </div>
  );
};

export default Celulas;
